import { ArrowRight } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useFeatureRequestDialog } from "@/contexts/FeatureRequestDialogContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

/**
 * Tiny brand-tinted pill rendered next to the gallery title — "Suggest an
 * idea →". Clicking opens the global FeatureRequestDialog with
 * `source = "post_generation"` so the resulting row + every PostHog event
 * tags this surface, letting us compare conversion against the footer +
 * Profile entry points.
 *
 * Hidden for anonymous users (the POST endpoint requires auth — a dead-end
 * click is worse UX than no badge).
 *
 * The arrow slides 2px on hover via `group-hover:translate-x-0.5`, and the
 * RTL Tailwind prefix mirrors it so the motion feels right in Hebrew.
 */
export function SuggestIdeaBadge() {
  const { t } = useLanguage();
  const { open } = useFeatureRequestDialog();
  const { user } = useAuth();

  if (!user) return null;

  return (
    <button
      type="button"
      onClick={() => open("post_generation")}
      className={cn(
        "group inline-flex items-center gap-1.5 rounded-full",
        "border border-brand/20 bg-brand/10 hover:bg-brand/15",
        "px-2.5 py-1 text-[11px] font-medium tracking-tight",
        "text-brand transition-colors",
      )}
    >
      <span>{t.featureRequest.galleryBadge.cta}</span>
      <ArrowRight
        className={cn(
          "w-3 h-3 transition-transform",
          "group-hover:translate-x-0.5",
          // Mirror the arrow + its motion in RTL so it points (and slides)
          // the natural reading direction.
          "rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5",
        )}
      />
    </button>
  );
}
